import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const Consumables = ({ axiosInstance }) => {
    // State for Stock Consumable Management
    const [consumables, setConsumables] = useState([]);
    const [isAddConsumableOpen, setIsAddConsumableOpen] = useState(false);
    const [isAddConsumableConfirmOpen, setIsAddConsumableConfirmOpen] = useState(false);
    const [isEditConsumableOpen, setIsEditConsumableOpen] = useState(false);
    const [selectedConsumable, setSelectedConsumable] = useState(null);
    const [newConsumables, setNewConsumables] = useState([{
        brand: '',
        model: '',
        itemDescription: '',
        itemCode: '',
        quantity: ''
    }]);
    const [siNumber, setSiNumber] = useState("");
    const [drNumber, setDrNumber] = useState("");
    const [supplier, setSupplier] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(null);

    // State for Brand Management
    const [brands, setBrands] = useState([]);
    const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");

    // State for Consumable Details Modal (Aggregation)
    const [isConsumableDetailsOpen, setIsConsumableDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelConsumables, setModelConsumables] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");
    const [mainWarehouseId, setMainWarehouseId] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
            fetchConsumables();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            const warehouses = response.data;
            const main = warehouses.find(w => w.name === "Main Warehouse") || warehouses[0];
            if (main) {
                setMainWarehouseId(main.id);
            }
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        }
    };

    const fetchConsumables = async () => {
        try {
            const response = await axiosInstance.get('/stock/consumables');
            setConsumables(response.data);
        } catch (error) {
            console.error("Error fetching consumables:", error);
            toast.error("Failed to fetch consumables");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/consumables/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleConsumableInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedConsumables = [...newConsumables];
        updatedConsumables[index] = {
            ...updatedConsumables[index],
            [name]: value
        };
        setNewConsumables(updatedConsumables);
    };

    const handleAddConsumableRow = () => {
        setNewConsumables([...newConsumables, {
            brand: '',
            model: '',
            itemDescription: '',
            itemCode: '',
            quantity: ''
        }]);
    };

    const handleRemoveConsumableRow = (index) => {
        if (newConsumables.length > 1) {
            const updatedConsumables = newConsumables.filter((_, i) => i !== index);
            setNewConsumables(updatedConsumables);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedConsumable(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveConsumable = async () => {
        // Basic validation
        for (const consumable of newConsumables) {
            if (!consumable.brand || !consumable.model || !consumable.itemCode || !consumable.quantity) {
                toast.error("Please fill in required fields for all rows");
                return;
            }
        }

        try {
            const toCreate = newConsumables.map(consumable => ({
                ...consumable,
                quantity: parseInt(consumable.quantity) || 0,
                si_number: siNumber,
                dr_number: drNumber,
                supplier: supplier,
                delivery_date: deliveryDate
            }));

            if (toCreate.length > 0) {
                await axiosInstance.post('/stock/consumables', toCreate);
            }

            toast.success("Consumables processed successfully");
            setIsAddConsumableOpen(false);
            setNewConsumables([{
                brand: '',
                model: '',
                itemDescription: '',
                itemCode: '',
                quantity: ''
            }]);
            setSiNumber("");
            setDrNumber("");
            setSupplier("");
            setDeliveryDate(null);
            fetchConsumables(); // Refresh list
        } catch (error) {
            console.error("Error saving consumables:", error);
            toast.error("Failed to save consumables");
        }
    };

    const handleRowClick = (aggregatedConsumable) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        // Use filteredConsumables instead of raw consumables to ensure consistency
        const relatedConsumables = filteredConsumables.filter(c => 
            normalize(c.model) === normalize(aggregatedConsumable.model) && 
            normalize(c.brand) === normalize(aggregatedConsumable.brand) &&
            normalize(c.itemDescription) === normalize(aggregatedConsumable.itemDescription) &&
            normalize(c.itemCode) === normalize(aggregatedConsumable.itemCode)
        );
        setSelectedModel(aggregatedConsumable);
        setModelConsumables(relatedConsumables);
        setIsConsumableDetailsOpen(true);
    };

    const handleEditConsumable = (consumable) => {
        setSelectedConsumable(consumable);
        setIsEditConsumableOpen(true);
    };

    const handleDeleteConsumable = async (consumableId) => {
        const confirmed = await confirmDialog({
            title: "Delete Consumable",
            description: "Are you sure you want to delete this consumable?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/consumables/${consumableId}`);
            toast.success("Consumable deleted successfully");
            
            // Refresh main list
            await fetchConsumables();
            
            // Update modal list
            setModelConsumables(prev => prev.filter(c => c.id !== consumableId));
            
            // If no consumables left for this model, close modal
            if (modelConsumables.length <= 1) {
                setIsConsumableDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting consumable:", error);
            toast.error("Failed to delete consumable");
        }
    };

    const handleUpdateConsumable = async () => {
        if (!selectedConsumable) return;
        
        try {
            const payload = {
                brand: selectedConsumable.brand,
                model: selectedConsumable.model,
                itemDescription: selectedConsumable.itemDescription,
                itemCode: selectedConsumable.itemCode,
                quantity: parseInt(selectedConsumable.quantity) || 0
            };

            await axiosInstance.put(`/stock/consumables/${selectedConsumable.id}`, payload);
            toast.success("Consumable updated successfully");
            setIsEditConsumableOpen(false);
            setSelectedConsumable(null);
            
            // Refresh main list
            await fetchConsumables();
            
            // Update local state for details modal if it's open
            if (isConsumableDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/consumables');
                const allConsumables = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelConsumables = allConsumables.filter(c => 
                    normalize(c.model) === normalize(selectedModel.model) && 
                    normalize(c.brand) === normalize(selectedModel.brand) &&
                    normalize(c.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(c.itemCode) === normalize(selectedModel.itemCode) &&
                    (!mainWarehouseId || !c.warehouse_id || c.warehouse_id === mainWarehouseId)
                );
                setModelConsumables(updatedModelConsumables);
            }
        } catch (error) {
            console.error("Error updating consumable:", error);
            toast.error("Failed to update consumable");
        }
    };

    const handleSaveBrand = async () => {
        if (!newBrandName.trim()) {
            toast.error("Brand name cannot be empty");
            return;
        }

        try {
            await axiosInstance.post('/stock/consumables/brands', { name: newBrandName.trim() });
            toast.success("Brand added successfully");
            setNewBrandName("");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error saving brand:", error);
            if (error.response && error.response.status === 400) {
                toast.error("Brand already exists");
            } else {
                toast.error("Failed to save brand");
            }
        }
    };

    const handleDeleteBrand = async (brandId) => {
        try {
            await axiosInstance.delete(`/stock/consumables/brands/${brandId}`);
            toast.success("Brand deleted successfully");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error deleting brand:", error);
            toast.error("Failed to delete brand");
        }
    };

    // Filter and Sort Consumables
    const filteredConsumables = consumables.filter(consumable => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? consumable.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (consumable.itemCode?.toLowerCase().includes(searchLower) || 
             consumable.itemDescription?.toLowerCase().includes(searchLower));
        
        // Filter by last 24 hours (1 day recent added items)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const matchesDate = consumable.createdAt && new Date(consumable.createdAt) >= oneDayAgo;

        // Filter by Main Warehouse (exclude transferred items)
        const matchesWarehouse = !mainWarehouseId || !consumable.warehouse_id || consumable.warehouse_id === mainWarehouseId;

        return matchesBrand && matchesSearch && matchesDate && matchesWarehouse;
    });

    // Aggregate Consumables by Brand + Model + Description + ItemCode
    const aggregatedConsumables = Object.values(filteredConsumables.reduce((acc, consumable) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(consumable.brand)}-${normalize(consumable.model)}-${normalize(consumable.itemDescription)}-${normalize(consumable.itemCode)}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...consumable,
                totalQuantity: 0,
                count: 0
            };
        } else {
            if (acc[key].si_number !== 'Multiple' && acc[key].si_number !== consumable.si_number) {
                acc[key].si_number = 'Multiple';
            }
            if (acc[key].dr_number !== 'Multiple' && acc[key].dr_number !== consumable.dr_number) {
                acc[key].dr_number = 'Multiple';
            }
        }
        acc[key].totalQuantity += (consumable.quantity || 0);
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedConsumables = aggregatedConsumables.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.totalQuantity - a.totalQuantity;
            case "lowest_qty":
                return a.totalQuantity - b.totalQuantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Consumables Inventory</h2>
            </div>

            {/* Split Layout: 30% Left, 70% Right */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-6">
                
                {/* Left Side: Controls */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    {/* Top Left Buttons */}
                    <div className="flex justify-start mb-4 gap-2">
                        <Button onClick={() => setIsAddConsumableOpen(true)} className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Consumables
                        </Button>
                        <Button onClick={() => setIsAddBrandOpen(true)} variant="outline" className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Brand
                        </Button>
                    </div>

                    {/* Filtering Section */}
                    <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                            <Label>Brand</Label>
                            <Select value={brandFilter} onValueChange={setBrandFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Brands</SelectItem>
                                    {brands.map(brand => (
                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Search</Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Search Item Code or Description..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Stock List Table */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Recent Activity</h3>
                        <div className="w-[200px]">
                            <Select value={sortOption} onValueChange={setSortOption}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sort By" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                                    <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                                    <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="border rounded-md flex-1 overflow-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead className="font-bold">Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Total Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedAggregatedConsumables.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                                            No consumables found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAggregatedConsumables.map((consumable, index) => (
                                        <TableRow 
                                            key={`${consumable.brand}-${consumable.model}-${index}`} 
                                            className="hover:bg-green-100 cursor-pointer transition-colors"
                                            onClick={() => handleRowClick(consumable)}
                                        >
                                            <TableCell>{consumable.si_number === 'Multiple' ? 'Multiple' : (consumable.si_number || '-')}</TableCell>
                                            <TableCell>{consumable.dr_number === 'Multiple' ? 'Multiple' : (consumable.dr_number || '-')}</TableCell>
                                            <TableCell className="font-bold">{consumable.brand}</TableCell>
                                            <TableCell>{consumable.model}</TableCell>
                                            <TableCell>{consumable.itemDescription}</TableCell>
                                            <TableCell>{consumable.itemCode}</TableCell>
                                            <TableCell>{consumable.totalQuantity}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Consumable Details Modal */}
            <Dialog open={isConsumableDetailsOpen} onOpenChange={setIsConsumableDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Consumable Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Entries: {modelConsumables.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Delivery Date</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelConsumables.map((consumable) => (
                                    <TableRow key={consumable.id}>
                                        {isSuperAdmin && <TableCell>{consumable.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(consumable.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{consumable.brand}</TableCell>
                                        <TableCell>{consumable.model}</TableCell>
                                        <TableCell>{consumable.itemDescription}</TableCell>
                                        <TableCell>{consumable.itemCode}</TableCell>
                                        <TableCell>{consumable.si_number || '-'}</TableCell>
                                        <TableCell>{consumable.dr_number || '-'}</TableCell>
                                        <TableCell>{consumable.supplier || '-'}</TableCell>
                                        <TableCell>{consumable.delivery_date ? new Date(consumable.delivery_date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>{consumable.quantity}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditConsumable(consumable)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteConsumable(consumable.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsConsumableDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Consumable Modal */}
            <Dialog open={isAddConsumableOpen} onOpenChange={setIsAddConsumableOpen}>
                <DialogContent className="max-w-[90vw] w-full max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add New Consumables</DialogTitle>
                        <DialogDescription>
                            Enter the details of the new consumable stock.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-3 gap-4 p-4 border-b">
                        <div className="grid gap-2">
                            <Label htmlFor="siNumber">SI Number</Label>
                            <Input
                                id="siNumber"
                                value={siNumber}
                                onChange={(e) => setSiNumber(e.target.value)}
                                placeholder="Enter SI Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="drNumber">DR Number</Label>
                            <Input
                                id="drNumber"
                                value={drNumber}
                                onChange={(e) => setDrNumber(e.target.value)}
                                placeholder="Enter DR Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="supplier">Supplier</Label>
                            <Input
                                id="supplier"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                                placeholder="Enter Supplier"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Date of Delivery</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={"outline"}
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !deliveryDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={deliveryDate}
                                        onSelect={setDeliveryDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto py-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {newConsumables.map((consumable, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Select 
                                                value={consumable.brand} 
                                                onValueChange={(value) => handleConsumableInputChange(index, { target: { name: 'brand', value } })}
                                            >
                                                <SelectTrigger className="w-[150px]">
                                                    <SelectValue placeholder="Brand" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {brands.map(brand => (
                                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="model"
                                                value={consumable.model}
                                                onChange={(e) => handleConsumableInputChange(index, e)}
                                                placeholder="Model"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemDescription"
                                                value={consumable.itemDescription}
                                                onChange={(e) => handleConsumableInputChange(index, e)}
                                                placeholder="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemCode"
                                                value={consumable.itemCode}
                                                onChange={(e) => handleConsumableInputChange(index, e)}
                                                placeholder="Item Code"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="quantity"
                                                type="number"
                                                value={consumable.quantity}
                                                onChange={(e) => handleConsumableInputChange(index, e)}
                                                placeholder="Qty"
                                                className="w-[100px]"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveConsumableRow(index)}
                                                disabled={newConsumables.length === 1}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleAddConsumableRow} variant="outline" size="sm" className="flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Add Row
                            </Button>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" onClick={() => setIsAddConsumableConfirmOpen(true)}>Add to Inventory</Button>
                    </DialogFooter>
                    <AlertDialog open={isAddConsumableConfirmOpen} onOpenChange={setIsAddConsumableConfirmOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Add to Inventory?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to add these items to inventory?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleSaveConsumable}>Confirm</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DialogContent>
            </Dialog>

            {/* Edit Consumable Modal */}
            <Dialog open={isEditConsumableOpen} onOpenChange={setIsEditConsumableOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Consumable</DialogTitle>
                        <DialogDescription>
                            Update the details of the consumable stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedConsumable && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedConsumable.brand} 
                                        onValueChange={(value) => setSelectedConsumable(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedConsumable.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedConsumable.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedConsumable.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedConsumable.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateConsumable}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Brand Modal */}
            <Dialog open={isAddBrandOpen} onOpenChange={setIsAddBrandOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add New Brand</DialogTitle>
                        <DialogDescription>
                            Enter a new brand name to add to the list.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="brand-name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="brand-name"
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter brand name..."
                            />
                        </div>
                        
                        <div className="mt-4 border rounded-md p-2 max-h-[200px] overflow-y-auto">
                            <Label className="mb-2 block text-sm font-medium">Brands</Label>
                            {brands.length === 0 ? (
                                <p className="text-sm text-gray-500">No brands found.</p>
                            ) : (
                                <ul className="text-sm space-y-1">
                                    {brands.map((brand) => (
                                        <li key={brand.id} className="flex justify-between items-center text-gray-700 p-1 hover:bg-gray-100 rounded">
                                            <span>• {brand.name}</span>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteBrand(brand.id)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" onClick={handleSaveBrand}>Save Brand</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Consumables;
